import { Program } from '../../types/program';

// One pre-aggregated row from the enrollment_breakdown() RPC. Each row is a unique
// (program, school, grade, is_active, payment_status) bucket with a count, so the
// Summary pivot can be rebuilt without loading every enrollment into the browser.
export interface BreakdownRow {
  program: Program;
  school: string;
  grade: string;
  is_active: boolean;
  payment_status: string;
  cnt: number;
}

export interface ProgramSummary {
  program: Program;
  totalEnrollments: number;
  activeEnrollments: number;
  inactiveEnrollments: number;
  pendingEnrollments: number;
  paidEnrollments: number;
  partialEnrollments: number;
  overdueEnrollments: number;
  processingEnrollments: number;
  voidedEnrollments: number;
}

export interface PivotRow {
  program: Program;
  total: number;
  activeEnrollments: number;
  inactiveEnrollments: number;
  paymentStatusCounts: Map<string, number>;
  schoolCounts: Map<string, number>;
  gradeCounts: Map<string, number>;
}

// The three Summary display toggles. Applied client-side over the small breakdown
// result so flipping a toggle never needs a refetch.
export interface BreakdownToggles {
  showInactivePrograms: boolean;
  showInactiveEnrollments: boolean;
  excludeVoided: boolean;
}

// Whether a breakdown row is visible under the current toggles. Mirrors the
// per-enrollment skip logic the old client-side Summary loops applied.
export function isBreakdownRowVisible(r: BreakdownRow, t: BreakdownToggles): boolean {
  return (
    (t.showInactivePrograms || r.program.active) &&
    (t.showInactiveEnrollments || r.is_active) &&
    !(t.excludeVoided && r.payment_status === 'voided')
  );
}

// Sort by program type (alphabetical) then by enrollment count descending — the
// ordering the Summary view has always used.
function byTypeThenCount<T extends { program: Program }>(getCount: (row: T) => number) {
  return (a: T, b: T) => {
    const typeA = a.program.type || 'other';
    const typeB = b.program.type || 'other';
    if (typeA !== typeB) return typeA.localeCompare(typeB);
    return getCount(b) - getCount(a);
  };
}

export function buildProgramSummaries(rows: BreakdownRow[], t: BreakdownToggles): ProgramSummary[] {
  const summaries = new Map<string, ProgramSummary>();

  rows.forEach(r => {
    if (!isBreakdownRowVisible(r, t)) return;

    const programId = r.program.id;
    if (!summaries.has(programId)) {
      summaries.set(programId, {
        program: r.program,
        totalEnrollments: 0,
        activeEnrollments: 0,
        inactiveEnrollments: 0,
        pendingEnrollments: 0,
        paidEnrollments: 0,
        partialEnrollments: 0,
        overdueEnrollments: 0,
        processingEnrollments: 0,
        voidedEnrollments: 0,
      });
    }

    const summary = summaries.get(programId)!;
    summary.totalEnrollments += r.cnt;

    // Payment-status buckets are only counted for active enrollments.
    if (r.is_active) {
      summary.activeEnrollments += r.cnt;
      switch (r.payment_status) {
        case 'pending': summary.pendingEnrollments += r.cnt; break;
        case 'paid': summary.paidEnrollments += r.cnt; break;
        case 'partial': summary.partialEnrollments += r.cnt; break;
        case 'overdue': summary.overdueEnrollments += r.cnt; break;
        case 'payment_processing': summary.processingEnrollments += r.cnt; break;
        case 'voided': summary.voidedEnrollments += r.cnt; break;
      }
    } else {
      summary.inactiveEnrollments += r.cnt;
    }
  });

  return Array.from(summaries.values()).sort(byTypeThenCount(s => s.totalEnrollments));
}

export function buildPivotData(rows: BreakdownRow[], t: BreakdownToggles): PivotRow[] {
  const programGroups = new Map<string, PivotRow>();

  rows.forEach(r => {
    if (!isBreakdownRowVisible(r, t)) return;

    const programId = r.program.id;
    if (!programGroups.has(programId)) {
      programGroups.set(programId, {
        program: r.program,
        total: 0,
        activeEnrollments: 0,
        inactiveEnrollments: 0,
        paymentStatusCounts: new Map(),
        schoolCounts: new Map(),
        gradeCounts: new Map(),
      });
    }

    const group = programGroups.get(programId)!;
    group.total += r.cnt;

    if (r.is_active) {
      group.activeEnrollments += r.cnt;
      // Payment statuses are only counted for active enrollments.
      group.paymentStatusCounts.set(r.payment_status, (group.paymentStatusCounts.get(r.payment_status) || 0) + r.cnt);
    } else {
      group.inactiveEnrollments += r.cnt;
    }

    // School / grade columns count both active and inactive (labels are server-normalized).
    group.schoolCounts.set(r.school, (group.schoolCounts.get(r.school) || 0) + r.cnt);
    group.gradeCounts.set(r.grade, (group.gradeCounts.get(r.grade) || 0) + r.cnt);
  });

  return Array.from(programGroups.values()).sort(byTypeThenCount(p => p.total));
}

// Distinct, sorted school / grade labels across all visible rows — the consistent
// column set for the Summary pivot.
export function collectBreakdownLabels(
  rows: BreakdownRow[],
  t: BreakdownToggles
): { schools: string[]; grades: string[] } {
  const schools = new Set<string>();
  const grades = new Set<string>();
  rows.forEach(r => {
    if (!isBreakdownRowVisible(r, t)) return;
    schools.add(r.school);
    grades.add(r.grade);
  });
  return {
    schools: Array.from(schools).sort(),
    grades: Array.from(grades).sort(),
  };
}
